/**
 * Athlete Client Service - Client-Safe Operations
 *
 * Provides athlete data operations that are safe to use in client components.
 * Extracted from athleteService.ts to fix next/headers bundling issues.
 *
 * Requirements summary:
 * - Athlete status summaries should only count current/upcoming-season team
 *   assignments so pre-season placements are visible and completed rosters are not.
 * - Private lesson summaries should only count current/future confirmed lessons
 *   as active participation; completed lessons feed status-history metadata.
 * - The org-roster fetch (`fetchAthletes`) now honors an ACTIVE `organization_staff`
 *   row, not just `auth_users.user_type` (plan 2026-06-02-001 / issue #637, U6/KTD9).
 *   A parent who was granted staff access keeps user_type='parent' but must see the
 *   full gym roster; a normal parent (no staff row) still gets own-family-only.
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/utils/supabase/client';
import { orgQueries } from '@/utils/supabase/organizationQueries';
import { createLogger } from '@/utils/logger/devLogger';
import { withOrganizationScope, hasActiveStaffRow, getAthleteIdsInOrganization } from '@/utils/supabase/organizationScope';
import { hasDemoViewerGrant } from '@/utils/demo/demoMembership';
import { fetchAllInChunks } from '@/utils/supabase/fetchInChunks';
import { fetchOrganizationRoster, fetchRosterByAthleteIds } from '@/services/athlete/athleteRosterReads';
import { getParentAthleteIds } from '@/utils/relationships/parentAthleteRelationships';
import { calculateAge, isValidUuid } from '@/utils/athletes/athleteUtils';
import { fetchActionableTeamSeasonIds } from '@/utils/seasons/teamSeasonVisibility';
import { formatDateOnly } from '@/utils/formatters/dateUtils';
import { byCreatedAtDesc } from '@/utils/formatters/byCreatedAtDesc';

import {
  Athlete,
  AthleteTeamData,
  AthleteClassData,
  AthleteMembershipData,
  AthletePrivateLessonData,
  AthleteServiceResult,
  AthleteServiceError
} from '@/utils/types/athlete';

const logger = createLogger('AthleteClientService');

// Moved out to keep this file under its line budget (#2669); re-exported so the
// long-standing import path keeps working.
export { fetchAthleteStatusMetadata, type AthleteStatusMetadata } from '@/services/athlete/athleteStatusMetadata';

/**
 * Who this signed-in user may see: the whole gym, or a specific set of athletes.
 *
 * A parent-typed account that was granted staff access (plan 2026-06-02-001 /
 * issue #637, U6/KTD9) keeps user_type='parent' but should see the full gym
 * roster — the same row-not-type rule the request-level auth uses. Only an
 * ACTIVE organization_staff row grants that; a normal parent (no staff row)
 * gets own-family-only, and an inactive (deactivated) staff row does NOT
 * qualify.
 */
async function resolveRosterScope(
  userType: string,
  userId: string,
  organizationId: string
): Promise<{ wholeGym: boolean; athleteIds: string[] }> {
  if (userType === 'gym_staff') return { wholeGym: true, athleteIds: [] };
  if (userType !== 'parent') return { wholeGym: false, athleteIds: [] };
  if (await hasActiveStaffRow(userId, organizationId)) return { wholeGym: true, athleteIds: [] };
  // A demo-gym viewer has no staff row — their read access is the demo_org_viewers
  // grant (#1354), the same rule the athlete-access gate follows. Without this a
  // parent-typed viewer resolves to own-family-only, which in the demo gym is nobody,
  // so the roster they were sent to browse comes back empty while individual profiles
  // open fine. Costs a real gym nothing: the helper returns false without a query for
  // any org that is not the shared demo gym. Read-only — this widens which athletes are
  // LISTED, never what may be changed.
  if (await hasDemoViewerGrant(organizationId, userId, supabase)) {
    return { wholeGym: true, athleteIds: [] };
  }

  const parentAthleteIds = await getParentAthleteIds(userId);
  // Only this gym's athletes: a family linked at another gym stays hidden.
  const orgAthleteIds = new Set(await getAthleteIdsInOrganization(organizationId));
  return {
    wholeGym: false,
    athleteIds: parentAthleteIds.filter(id => orgAthleteIds.has(id)),
  };
}

/**
 * Fetches all athletes for an organization
 */
export async function fetchAthletes(organizationId: string, isSwitchingOrganization?: boolean): Promise<AthleteServiceResult> {
  try {
    if (isSwitchingOrganization) {
      return { data: [], totalCount: 0, error: { type: 'UNKNOWN', message: 'Switching organizations...', canRetry: true } };
    }

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return { data: [], totalCount: 0, error: { type: 'PERMISSION_DENIED', message: 'No session', canRetry: true } };

    const userId = session.user.id;
    const { data: userData } = await supabase.from('auth_users').select('user_type').eq('id', userId).single();
    if (!userData) return { data: [], totalCount: 0, error: { type: 'PERMISSION_DENIED', message: 'No user data', canRetry: true } };

    // A whole-gym roster is filtered server-side through the
    // athlete_organizations join, so no id list travels in the URL; an
    // own-family roster is a small, client-computed id list that gets chunked.
    const scope = await resolveRosterScope(userData.user_type, userId, organizationId);
    if (!scope.wholeGym && scope.athleteIds.length === 0) return { data: [], totalCount: 0 };

    const rows = scope.wholeGym
      ? await fetchOrganizationRoster(organizationId)
      : await fetchRosterByAthleteIds(scope.athleteIds);

    if (rows.error) throw rows.error;

    // Newest first. The reads above page by `id` so PostgREST returns whole
    // rosters (its 1,000-row cap would otherwise truncate a big gym silently),
    // which means display order is applied here rather than by the database.
    const sorted = [...rows.data].sort((a: any, b: any) => byCreatedAtDesc(a, b));

    return {
      data: sorted.map((athlete: any) => ({
        ...athlete,
        skill_level: athlete.athlete_skill_levels?.[0],
        athlete_skill_levels: undefined,
        // Join rows exist only to filter server-side; callers expect an
        // athlete, not an athlete carrying its membership rows.
        athlete_organizations: undefined
      })) as Athlete[],
      totalCount: sorted.length
    };
  } catch (error: any) {
    logger.error('Error fetching athletes:', {
      error: error?.message || 'Unknown error',
      code: error?.code,
      hint: error?.hint,
    });
    return { data: [], totalCount: 0, error: { type: 'UNKNOWN', message: error.message, canRetry: true } };
  }
}

/**
 * Gets teams for an athlete
 */
export async function getTeamsByAthlete(athleteId: string) {
  const { data: teamRelations } = await supabase.from('team_athletes').select('team_id').eq('athlete_id', athleteId);
  if (!teamRelations?.length) return [];
  const teamIds = teamRelations.map(r => r.team_id);
  const { data: teams } = await supabase.from('teams').select('*').in('id', teamIds);
  return teams || [];
}

/**
 * Gets draft athletes
 */
export async function fetchDraftAthletes() {
  const { data } = await supabase.from('athletes').select('*').eq('is_draft', true).order('created_at', { ascending: false });
  return data || [];
}

/*
 * deleteDraftAthlete() was removed with #3082. It ran
 * `.from('athletes').delete()` on the RLS-scoped BROWSER client, which is the
 * capability that migration 20260827_001 revokes — a client delete of an
 * `athletes` row carries none of the guards in
 * DELETE /api/athletes/[athleteId]/staff-delete, and `athletes` rows are shared
 * across gyms. Nothing in this repo sets `is_draft: true` (the only writer,
 * promote-draft/route.ts:108, sets it FALSE), and production held 0 draft rows
 * of 976 athletes when this was measured on 2026-08-18, so the path was dead.
 * If drafts are ever revived, delete them through a service-role route the way
 * convertDraftToAthlete already does — not from the browser.
 */

/**
 * Converts a draft athlete to a regular athlete.
 *
 * GH #883 U4: promotion activates a roster athlete, so the write moved
 * behind POST /api/athletes/[id]/promote-draft where the roster-cap guard
 * runs server-side (R9 forbids client-side enforcement). Same signature and
 * null-on-failure contract as the old direct write.
 */
export async function convertDraftToAthlete(draftId: string, organizationId: string) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const response = await fetch(`/api/athletes/${draftId}/promote-draft`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
      },
      body: JSON.stringify({ organizationId }),
    });

    const result = await response.json().catch(() => null);
    if (!response.ok || !result?.athlete) {
      logger.error('Draft promotion failed:', result?.message || result?.error || response.status);
      return null;
    }
    return result.athlete;
  } catch (error) {
    logger.error('Draft promotion request failed:', error);
    return null;
  }
}

/**
 * Bulk fetch athlete-team relationships
 */
// #833: `client` defaults to the RLS browser client (existing callers unchanged);
// server callers (the notification recipient resolver) pass a service-role client
// so the same participation logic runs without a session. Same pattern on every
// fetcher in this file + fetchAthleteParticipationContexts.
export async function fetchAthleteTeamsData(athleteIds: string[], organizationId: string, client: SupabaseClient = supabase): Promise<AthleteTeamData> {
  const result: AthleteTeamData = { athleteTeamsMap: new Map(), allTeams: new Map() };
  if (!athleteIds.length) return result;

  const { seasonIds: actionableSeasonIds, error: seasonError } = await fetchActionableTeamSeasonIds(client, organizationId);
  if (seasonError) {
    logger.warn('Failed to fetch actionable seasons for athlete team summary:', seasonError);
  }

  const actionableSeasonIdList = [...actionableSeasonIds];
  if (actionableSeasonIdList.length === 0) return result;

  const { rows: teamRelations } = await fetchAllInChunks<any>(athleteIds, (chunk, from, to) =>
    client
      .from('team_athletes')
      .select('athlete_id, team_id, status')
      .in('athlete_id', chunk)
      .in('status', ['active', 'pending_payment', 'pending_acceptance'])
      .neq('visible_on_roster', false)
      .order('id', { ascending: true })
      .range(from, to)
  );
  if (!teamRelations.length) return result;

  const teamIds = [...new Set(teamRelations.map(r => r.team_id))];
  const { data: teams } = await withOrganizationScope(
    client.from('teams').select('*').in('season_id', actionableSeasonIdList),
    organizationId,
    { table: 'teams' }
  );

  const relevantTeams = (teams || []).filter(t => teamIds.includes(t.id));
  relevantTeams.forEach(t => result.allTeams.set(t.id, t));

  teamRelations.forEach(r => {
    const team = result.allTeams.get(r.team_id);
    if (team) {
      if (!result.athleteTeamsMap.has(r.athlete_id)) result.athleteTeamsMap.set(r.athlete_id, []);
      result.athleteTeamsMap.get(r.athlete_id)!.push({ ...team, assignment_status: r.status });
    }
  });

  return result;
}

/**
 * Bulk fetch athlete-class relationships
 */
export async function fetchAthleteClassesData(athleteIds: string[], organizationId: string, client: SupabaseClient = supabase): Promise<AthleteClassData> {
  const result: AthleteClassData = { athleteClassesMap: new Map(), allClasses: new Map() };
  if (!athleteIds.length) return result;

  const { rows: enrollments } = await fetchAllInChunks<any>(athleteIds, (chunk, from, to) =>
    client
      .from('class_enrollments')
      .select('athlete_id, class_section_id, status, class_sections!inner(id, section_name, location_id, class_program_id, programs!inner(id, name, program_type))')
      .in('athlete_id', chunk)
      .eq('organization_id', organizationId)
      .eq('status', 'active')
      .order('id', { ascending: true })
      .range(from, to)
  );

  enrollments.forEach((e: any) => {
    const section = e.class_sections;
    const program = section.programs;
    if (!result.allClasses.has(section.class_program_id)) {
      result.allClasses.set(section.class_program_id, { id: section.class_program_id, name: program.name, program_type: program.program_type, location_id: section.location_id, section_name: section.section_name });
    }
    if (!result.athleteClassesMap.has(e.athlete_id)) result.athleteClassesMap.set(e.athlete_id, []);
    result.athleteClassesMap.get(e.athlete_id)!.push({ ...result.allClasses.get(section.class_program_id)!, status: e.status });
  });

  return result;
}

/**
 * Bulk fetch memberships
 */
export async function fetchAthleteMembershipsData(athleteIds: string[], organizationId: string, client: SupabaseClient = supabase): Promise<AthleteMembershipData> {
  const result: AthleteMembershipData = { athleteMembershipsMap: new Map() };
  if (!athleteIds.length) return result;

  const { rows } = await fetchAllInChunks<any>(athleteIds, (chunk, from, to) =>
    client.from('athlete_location_memberships').select('*').in('athlete_id', chunk).eq('organization_id', organizationId).eq('membership_status', 'active')
      .order('id', { ascending: true }).range(from, to)
  );
  rows.forEach(m => {
    if (!result.athleteMembershipsMap.has(m.athlete_id)) result.athleteMembershipsMap.set(m.athlete_id, []);
    result.athleteMembershipsMap.get(m.athlete_id)!.push(m);
  });
  return result;
}

/**
 * Bulk fetch private lessons
 *
 * `asOfDate` lets a caller pin the "upcoming" boundary to a specific YYYY-MM-DD
 * day instead of the browser-local today. It defaults to browser-local today so
 * every existing caller is unchanged; the archive-impact path (#963) passes the
 * GYM-timezone today so its "upcoming lesson" count matches the nightly status
 * job, which evaluates lessons in the gym's timezone (browser TZ ≠ gym TZ could
 * otherwise shift a lesson dated exactly "today" by a calendar day).
 */
export async function fetchAthletePrivateLessonsData(athleteIds: string[], organizationId: string, client: SupabaseClient = supabase, asOfDate?: string): Promise<AthletePrivateLessonData> {
  const result: AthletePrivateLessonData = { athletePrivateLessonsMap: new Map() };
  if (!athleteIds.length) return result;

  const today = asOfDate ?? formatDateOnly(new Date());
  const { rows } = await fetchAllInChunks<any>(athleteIds, (chunk, from, to) =>
    client
      .from('private_lesson_bookings')
      .select('*')
      .in('athlete_id', chunk)
      .eq('organization_id', organizationId)
      .in('status', ['confirmed', 'scheduled'])
      .gte('lesson_date', today)
      .order('id', { ascending: true })
      .range(from, to)
  );
  rows.forEach(b => {
    if (!result.athletePrivateLessonsMap.has(b.athlete_id)) result.athletePrivateLessonsMap.set(b.athlete_id, []);
    result.athletePrivateLessonsMap.get(b.athlete_id)!.push(b);
  });
  return result;
}

/**
 * Deleting an athlete is deliberately NOT available here.
 *
 * The only staff delete is `/api/athletes/[athleteId]/staff-delete`, which is
 * where the owner/admin role list, the has-history guard, and the multi-gym
 * checks live — and that route is itself parked behind
 * ENABLE_STAFF_ATHLETE_RECORD_ACTIONS (default off since 2026-08-06, #3080).
 *
 * A client-side delete cannot enforce any of that: the `athletes_delete_via_cache`
 * policy only asks for a staff access-cache row, so any staff role would be let
 * through. Note that removing the client helper closes the code half of #3082,
 * not the database half — that policy is unchanged, so a direct PostgREST delete
 * would still get through. Do not add a client-side delete back here.
 */

/**
 * Create a service for working with Athlete entities (Client-Safe)
 */
export const createAthleteService = (organizationId: string) => {
  return {
    getAll: async () => {
      const { data, error } = await orgQueries.athletes(organizationId);
      const athletes = data?.map((item: any) => ({
        ...item.athletes,
        base_status: item.athletes.status,
        organization_status: item.status,
        status: item.status,
        organization_created_at: item.created_at
      })) || [];
      return { data: athletes as unknown as Athlete[], error, success: !error };
    },
    getById: async (id: string) => {
      const { data, error } = await orgQueries.athletes(organizationId);
      const athlete = data?.map((item: any) => ({
        ...item.athletes,
        base_status: item.athletes.status,
        organization_status: item.status,
        status: item.status,
        organization_created_at: item.created_at
      })).find((a: any) => a.id === id);
      return { data: athlete as unknown as Athlete || null, error: null, success: !!athlete };
    }
  };
};

/**
 * Checks if an email is already in use by another athlete
 */
export async function isEmailInUse(email: string, organizationId: string, excludeAthleteId?: string): Promise<boolean> {
  try {
    if (!email) return false;

    let query = supabase
      .from('athletes')
      .select('id, email')
      .eq('email', email.toLowerCase().trim());

    if (excludeAthleteId) {
      query = query.neq('id', excludeAthleteId);
    }

    const { data, error } = await query;

    if (error) {
      logger.error('Error checking email:', error);
      return false;
    }

    // Filter by organization through athlete_organizations
    if (data && data.length > 0) {
      const athleteIds = data.map(athlete => athlete.id);
      const { data: orgData, error: orgError } = await supabase
        .from('athlete_organizations')
        .select('athlete_id')
        .in('athlete_id', athleteIds)
        .eq('organization_id', organizationId);

      if (orgError) {
        logger.error('Error checking organization membership:', orgError);
        return false;
      }

      return orgData && orgData.length > 0;
    }

    return false;
  } catch (error) {
    logger.error('Unexpected error:', error);
    return false;
  }
}
